import { displayImportedText } from '../services/displayLabels';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Card, CardBody, CardTitle, Col, Form, FormGroup, Input, Label,
  Row, Spinner
} from 'reactstrap';
import { BIASerial, toApiMeasurement, DEFAULT_ACQUISITION_PROFILE, BIA_ACQUISITION_PROFILES } from './BiaSerial';
import { BiaResults } from './BiaResults';
import { patientsApi, devicesApi, measurementsApi } from '../services/api';

const SEX_OPTIONS = [
  { value: 0, label: 'Maschio' },
  { value: 1, label: 'Femmina' }
];

const BUILD_OPTIONS = [
  { value: 0, label: 'Normale' },
  { value: 1, label: 'Sovrappeso' },
  { value: 2, label: 'Sportivo/a' }
];

// Il backend serializza gli enum come stringhe; il frontend li gestisce come numeri per i <select>.
const sexToNumber = (sex) => sex === 'Female' ? 1 : 0;
const bodyBuildToNumber = (build) => ['Normal', 'Overweight', 'Athletic'].indexOf(build ?? 'Normal');
const numberToSex = (n) => Number(n) === 1 ? 'Female' : 'Male';
const numberToBodyBuild = (n) => ['Normal', 'Overweight', 'Athletic'][Number(n)] ?? 'Normal';

export function missingBiaContext(patient) {
  if (!patient) return ['paziente salvato'];
  const missing = [];
  if (!patient.dateOfBirth || !Number.isFinite(Date.parse(patient.dateOfBirth)) || new Date(patient.dateOfBirth) > new Date()) missing.push('data di nascita valida');
  if (!(Number(patient.heightCm) > 0)) missing.push('altezza');
  if (!['Male', 'Female', 0, 1].includes(patient.sex)) missing.push('sesso');
  return missing;
}

const emptyPatient = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  sex: 0,
  heightCm: '',
  bodyBuild: 0,
  waistCircumferenceCm: '',
  hipCircumferenceCm: '',
  weightKg: ''
};

function formatDateForInput(dateValue) {
  if (!dateValue) return '';
  const text = String(dateValue).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return text;
}

export function BiaAnalyzer({ patientId: fixedPatientId = '', visitId = null, initialWeightKg = '', embedded = false, onMeasurementSaved = null }) {
  const biaRef = useRef(new BIASerial());
  const logEndRef = useRef(null);

  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(fixedPatientId);
  const [patientForm, setPatientForm] = useState(emptyPatient);
  const [patientSaving, setPatientSaving] = useState(false);

  const [deviceInfo, setDeviceInfo] = useState(null);
  const [deviceBackendId, setDeviceBackendId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [calibrating, setCalibrating] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [acquisitionProfile, setAcquisitionProfile] = useState(DEFAULT_ACQUISITION_PROFILE);
  const [activeFrequency, setActiveFrequency] = useState(null);
  const [measureStatus, setMeasureStatus] = useState('');

  const [lastMeasurement, setLastMeasurement] = useState(null);
  const [lastComposition, setLastComposition] = useState(null);
  const [pendingCapture, setPendingCapture] = useState(null);

  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);

  const bia = biaRef.current;
  const serialSupported = BIASerial.isSupported();
  const selectedPatient = patients.find(patient => patient.id === selectedPatientId);
  const missingContext = missingBiaContext(selectedPatient);
  const patientUrl = id => `/patients/${encodeURIComponent(id)}?tab=measurements`;

  useEffect(() => {
    if (!pendingCapture) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingCapture]);

  const addLog = useCallback((msg) => {
    setLogs(prev => [...prev, msg]);
  }, []);

  useEffect(() => {
    bia.onLog = addLog;
    bia.onTransportError = transportError => {
      setConnected(false);
      setDeviceInfo(null);
      setDeviceBackendId(null);
      setError(transportError.message);
    };
    return () => { bia.onLog = null; bia.onTransportError = null; };
  }, [bia, addLog]);

  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const loadPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const data = fixedPatientId
        ? [await patientsApi.get(fixedPatientId)]
        : await patientsApi.getAll();
      setPatients(data || []);
      if (fixedPatientId && data?.[0]) {
        const p = data[0];
        setSelectedPatientId(p.id);
        setPatientForm({
          firstName: p.firstName || '',
          lastName: p.lastName || '',
          dateOfBirth: formatDateForInput(p.dateOfBirth),
          sex: sexToNumber(p.sex),
          heightCm: p.heightCm ?? '',
          bodyBuild: bodyBuildToNumber(p.bodyBuild),
          waistCircumferenceCm: p.waistCircumferenceCm ?? '',
          hipCircumferenceCm: p.hipCircumferenceCm ?? '',
          weightKg: initialWeightKg ?? ''
        });
      }
    } catch (err) {
      setError(`Errore caricamento pazienti: ${err.message}`);
    } finally {
      setPatientsLoading(false);
    }
  }, [fixedPatientId, initialWeightKg]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  const handlePatientSelect = (e) => {
    const id = e.target.value;
    setSelectedPatientId(id);
    if (!id) {
      setPatientForm(emptyPatient);
      return;
    }
    const p = patients.find(x => x.id === id);
    if (p) {
      setPatientForm({
        firstName: p.firstName || '',
        lastName: p.lastName || '',
        dateOfBirth: formatDateForInput(p.dateOfBirth),
        sex: sexToNumber(p.sex),
        heightCm: p.heightCm ?? '',
        bodyBuild: bodyBuildToNumber(p.bodyBuild),
        waistCircumferenceCm: p.waistCircumferenceCm ?? '',
        hipCircumferenceCm: p.hipCircumferenceCm ?? '',
        weightKg: ''
      });
    }
  };

  const updateField = (field, value) => {
    setPatientForm(prev => ({ ...prev, [field]: value }));
  };

  const validatePatient = () => {
    const p = patientForm;
    if (!p.firstName.trim() || !p.lastName.trim()) return 'Nome e cognome sono obbligatori.';
    if (!p.heightCm || p.heightCm < 50 || p.heightCm > 250) return 'Altezza non valida.';
    return null;
  };

  const savePatient = async () => {
    const validation = validatePatient();
    if (validation) {
      setError(validation);
      return;
    }
    setPatientSaving(true);
    setError(null);
    try {
      const payload = {
        firstName: patientForm.firstName.trim(),
        lastName: patientForm.lastName.trim(),
        code: null,
        dateOfBirth: patientForm.dateOfBirth || null,
        sex: numberToSex(patientForm.sex),
        heightCm: Number(patientForm.heightCm),
        bodyBuild: numberToBodyBuild(patientForm.bodyBuild),
        waistCircumferenceCm: patientForm.waistCircumferenceCm ? Number(patientForm.waistCircumferenceCm) : null,
        hipCircumferenceCm: patientForm.hipCircumferenceCm ? Number(patientForm.hipCircumferenceCm) : null,
        notes: null
      };

      let saved;
      if (selectedPatientId) {
        saved = await patientsApi.update(selectedPatientId, { ...payload, isActive: true });
      } else {
        saved = await patientsApi.create(payload);
      }
      setSelectedPatientId(saved.id);
      await loadPatients();
      addLog(`Paziente ${saved.lastName} ${saved.firstName} salvato`);
    } catch (err) {
      setError(`Errore salvataggio paziente: ${err.message}`);
    } finally {
      setPatientSaving(false);
    }
  };

  const connectDevice = async () => {
    setConnecting(true);
    setError(null);
    try {
      await bia.connect();
      setConnected(true);
      addLog('Dispositivo connesso');

      const info = await bia.getDeviceInfo();
      setDeviceInfo(info);

      // Registra o recupera il dispositivo sul backend
      try {
        let device = null;
        try {
          device = await devicesApi.getBySerial(info.serialNumber);
        } catch {
          device = null;
        }
        if (!device) {
          device = await devicesApi.create({
            serialNumber: info.serialNumber,
            model: info.model,
            firmwareVersion: info.firmware,
            calibrationValue: info.calibrationValue || 0,
            isCalibrated: !!info.isCalibrated,
            isSupported: info.isSupported ?? true,
            batteryLevel: info.battery ?? 0,
            notes: null
          });
          addLog(`Dispositivo registrato: ${device.model} ${device.serialNumber}`);
        } else {
          addLog(`Dispositivo trovato: ${device.model} ${device.serialNumber}`);
        }
        setDeviceBackendId(device.id);
      } catch (err) {
        addLog(`Avviso: impossibile sincronizzare dispositivo con backend: ${err.message}`);
      }
    } catch (err) {
      setConnected(false);
      if (err?.name === 'NotFoundError') {
        setError('Nessun dispositivo selezionato. Collega o abbina il BioSmart, poi premi di nuovo “Collega BioSmart”.');
      } else {
        setError(`Connessione non riuscita: ${err.message}`);
      }
      addLog(`Connessione fallita: ${err.message}`);
    } finally {
      setConnecting(false);
    }
  };

  const disconnectDevice = async () => {
    try {
      await bia.disconnect();
    } catch {}
    setConnected(false);
    setDeviceInfo(null);
    setDeviceBackendId(null);
    setLastMeasurement(null);
    setLastComposition(null);
  };

  const handleCalibrate = async () => {
    if (!connected) return;
    setCalibrating(true);
    setError(null);
    try {
      const ok = await bia.calibrate();
      if (ok) {
        setDeviceInfo(prev => prev ? { ...prev, isCalibrated: true } : prev);
        addLog('Calibrazione completata');
      } else {
        setError('Calibrazione fallita');
      }
    } catch (err) {
      setError(`Errore calibrazione: ${err.message}`);
      addLog(`Errore calibrazione: ${err.message}`);
    } finally {
      setCalibrating(false);
    }
  };

  const handleMeasure = async () => {
    if (measuring || calibrating || patientSaving) return;
    if (!connected) {
      setError('Connettere prima il dispositivo.');
      return;
    }
    if (!selectedPatientId) {
      setError('Selezionare o creare un paziente prima di misurare.');
      return;
    }
    if (!patientForm.weightKg || patientForm.weightKg <= 0) {
      setError('Inserire il peso attuale del paziente.');
      return;
    }
    if (missingContext.length) { setError(`Completa nella cartella: ${missingContext.join(', ')}.`); return; }
    if (deviceInfo?.isSupported === false || deviceInfo?.isCalibrated === false) return;
    if (pendingCapture && !window.confirm('La lettura precedente non ha un salvataggio confermato. Hai verificato lo storico o conservato il file e vuoi acquisirne una nuova?')) return;
    setPendingCapture(null);

    setMeasuring(true);
    setActiveFrequency(BIA_ACQUISITION_PROFILES[acquisitionProfile][0]);
    setMeasureStatus(`Misura ${BIA_ACQUISITION_PROFILES[acquisitionProfile][0]} kHz in corso...`);
    setError(null);
    setLastMeasurement(null);
    setLastComposition(null);

    try {
      const raw = await bia.measure(frequency => {
        setActiveFrequency(frequency);
        setMeasureStatus(`Misura ${frequency} kHz in corso...`);
      }, acquisitionProfile);
      const measurement = {
        patientId: selectedPatientId,
        deviceId: deviceBackendId,
        visitId,
        weightKg: Number(patientForm.weightKg),
        ...toApiMeasurement(raw),
        notes: null
      };

      setMeasureStatus('Salvataggio misurazione e calcolo composizione...');
      setActiveFrequency(null);

      let saved;
      let composition;
      try {
        saved = await measurementsApi.create(measurement);
        composition = saved.bodyComposition;
        addLog('Misurazione salvata sul backend');
      } catch (apiErr) {
        setPendingCapture({ raw, payload: measurement, patientName: selectedPatientName });
        addLog(`SALVATAGGIO NON CONFERMATO: ${apiErr.message}`);
        throw new Error(`Lettura acquisita, salvataggio non confermato: ${apiErr.message}`);
      }

      setLastMeasurement({ ...saved, acquisitionProfile: raw.acquisitionProfile });
      setLastComposition(composition);
      setMeasureStatus('Misura completata');
      if (onMeasurementSaved) onMeasurementSaved(saved);
    } catch (err) {
      setError(`Errore misura: ${err.message}`);
      setMeasureStatus(`Errore: ${err.message}`);
      addLog(`Errore misura: ${err.message}`);
    } finally {
      setMeasuring(false);
      setActiveFrequency(null);
    }
  };

  const handleExport = () => {
    const data = {
      timestamp: lastMeasurement?.measuredAt || new Date().toISOString(),
      device: deviceInfo,
      patient: { ...patientForm, id: selectedPatientId },
      rawMeasurement: lastMeasurement,
      bodyComposition: lastComposition
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bia_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    addLog('Risultati esportati in JSON');
  };

  const handleNewMeasure = () => {
    setLastMeasurement(null);
    setLastComposition(null);
    setMeasureStatus('');
  };

  const exportPendingCapture = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify({ ...pendingCapture, status: 'save-unconfirmed' }, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a'); link.href = url; link.download = 'biosmart-lettura-da-verificare.json'; link.click(); URL.revokeObjectURL(url);
  };

  const validWeight = Number(patientForm.weightKg) >= 5 && Number(patientForm.weightKg) <= 300;
  const canMeasure = connected && !measuring && !calibrating && !patientSaving && !patientsLoading && selectedPatientId && validWeight && !missingContext.length && deviceInfo?.isSupported !== false && deviceInfo?.isCalibrated !== false;

  const deviceBadgeColor = connected ? 'success' : 'secondary';
  const deviceBadgeText = connected ? 'Connesso' : 'Disconnesso';

  const selectedPatientName = useMemo(() => {
    if (!selectedPatientId) return 'Nessun paziente selezionato';
    const p = patients.find(x => x.id === selectedPatientId);
    return p ? `${p.lastName} ${p.firstName}` : 'Paziente sconosciuto';
  }, [selectedPatientId, patients]);

  return (
    <div className="bia-analyzer">
      {!embedded && <header className="bia-header">
        <h1>BIA <span>Analyzer</span></h1>
        <Badge color={deviceBadgeColor} className="bia-device-badge" pill>
          <span className={`bia-dot ${connected ? 'connected' : ''}`} />
          {deviceBadgeText}
        </Badge>
      </header>}

      {error && (
        <Alert color="danger" toggle={() => setError(null)} className="mb-3">
          {error}
        </Alert>
      )}

      <section className="bia-readiness" aria-label="Preparazione della misura">
        <h2>{lastMeasurement ? 'Misura salvata nella cartella' : measuring ? 'Acquisizione in corso' : 'Prepara la misura BIA'}</h2>
        <p>{selectedPatientName}{visitId ? ' · Visita corrente' : ''}</p>
        <ul>
          <li><strong>BioSmart</strong><span>{!serialSupported ? 'Apri Chrome o Edge sul computer dello strumento' : connecting ? 'Collegamento in corso' : !connected ? 'Da collegare' : deviceInfo?.isSupported === false ? 'Modello non supportato' : deviceInfo?.isCalibrated === false ? 'Da calibrare' : 'Collegato'}</span></li>
          <li><strong>Dati in cartella</strong><span>{patientsLoading ? 'Caricamento' : missingContext.length ? `Da completare: ${missingContext.join(', ')}` : 'Disponibili per il calcolo'}</span></li>
          <li><strong>Peso della visita</strong><span>{validWeight ? `${Number(patientForm.weightKg).toLocaleString('it-IT')} kg` : 'Inserisci un peso tra 5 e 300 kg'}</span></li>
        </ul>
        {!!missingContext.length && selectedPatientId && <a href={`/patients/${encodeURIComponent(selectedPatientId)}?tab=general`}>Completa i dati nella cartella</a>}
        {lastMeasurement && <a className="btn btn-primary" href={`${patientUrl(lastMeasurement.patientId)}&measurement=${encodeURIComponent(lastMeasurement.id)}`}>Apri referto e stampa</a>}
      </section>

      {pendingCapture && <section className="bia-capture-recovery" aria-label="Recupero lettura acquisita">
        <h2>Lettura acquisita, salvataggio da verificare</h2>
        <p>I dati di {pendingCapture.patientName} restano in questa pagina. Prima di ripetere la misura, verifica se compare nello storico: il server potrebbe averla ricevuta anche se la risposta non è arrivata.</p>
        <div><a className="btn btn-primary" href={patientUrl(pendingCapture.payload.patientId)} target="_blank" rel="noreferrer">Verifica nello storico (nuova scheda)</a><Button outline color="secondary" onClick={exportPendingCapture}>Conserva lettura su questo computer</Button></div>
        <p className="small">Il file contiene dati della misura e del paziente. Non è un referto e non conferma il salvataggio.</p>
      </section>}

      {/* Step 1: Connessione */}
      <Card className="bia-card">
        <CardBody>
          <CardTitle tag="h2">1. Connessione dispositivo</CardTitle>
          <p className="text-muted">
            Collega il BioSmart tramite USB oppure abbinalo al Bluetooth di questo computer. Al primo accesso il browser ti chiederà quale dispositivo usare.
          </p>
          <div className="d-flex gap-2 flex-wrap">
            <Button color="primary" onClick={connectDevice} disabled={!serialSupported || connected || connecting}>
              {connecting ? <><Spinner size="sm" className="me-1" /> Collegamento...</> : 'Collega BioSmart'}
            </Button>
            <Button color="secondary" outline onClick={disconnectDevice} disabled={!connected || measuring || calibrating}>
              Disconnetti
            </Button>
          </div>

          {deviceInfo && (
            <div className="bia-info-panel mt-3">
              <Row>
                <Col xs="6" md="4" className="mb-2">
                  <div className="text-uppercase text-muted small">Modello</div>
                  <div className="fw-semibold">{deviceInfo.model || '-'}</div>
                </Col>
                <Col xs="6" md="4" className="mb-2">
                  <div className="text-uppercase text-muted small">Seriale</div>
                  <div className="fw-semibold">{deviceInfo.serialNumber || '-'}</div>
                </Col>
                <Col xs="6" md="4" className="mb-2">
                  <div className="text-uppercase text-muted small">Firmware</div>
                  <div className="fw-semibold">{deviceInfo.firmware || '-'}</div>
                </Col>
                <Col xs="6" md="4" className="mb-2">
                  <div className="text-uppercase text-muted small">Batteria</div>
                  <div className="fw-semibold">
                    {deviceInfo.battery !== undefined ? `${(deviceInfo.battery * 100).toFixed(0)}%` : '-'}
                  </div>
                </Col>
                <Col xs="6" md="4" className="mb-2">
                  <div className="text-uppercase text-muted small">Calibrazione</div>
                  <div className={`fw-semibold ${deviceInfo.isCalibrated ? 'text-success' : 'text-warning'}`}>
                    {deviceInfo.isCalibrated ? 'OK' : 'Non calibrato'}
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {!serialSupported && (
            <Alert color="warning" className="mt-3 mb-0">
              Il collegamento diretto non è disponibile in questo browser. Apri Sibilla con Chrome o Edge su un computer Windows, macOS o Linux.
            </Alert>
          )}

          {serialSupported && !connected && !connecting && (
            <Alert color="info" className="mt-3 mb-0">
              Dispositivo non collegato. Accendilo, verifica che sia collegato via USB o associato via Bluetooth, quindi premi <strong>Collega BioSmart</strong>.
            </Alert>
          )}
        </CardBody>
      </Card>

      {/* Step 2: Paziente */}
      {!embedded && <Card className="bia-card">
        <CardBody>
          <CardTitle tag="h2">2. Dati paziente</CardTitle>

          <FormGroup className="mb-3">
            <Label for="patientSelect">Paziente</Label>
            <Input
              id="patientSelect"
              type="select"
              value={selectedPatientId}
              onChange={handlePatientSelect}
              disabled={!connected || patientsLoading || measuring || calibrating}
            >
              <option value="">{patientsLoading ? 'Caricamento...' : 'Nuovo paziente'}</option>
              {patients.map(p => (
                <option key={p.id} value={p.id}>{p.lastName} {p.firstName} {p.code ? `(${displayImportedText(p.code)})` : ''}</option>
              ))}
            </Input>
          </FormGroup>

          {selectedPatientId && (
            <div className="mb-3 text-muted small">
              Paziente selezionato: <strong>{selectedPatientName}</strong>
            </div>
          )}

          <Form>
            <fieldset disabled={measuring || calibrating}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="firstName">Nome</Label>
                  <Input
                    id="firstName"
                    value={patientForm.firstName}
                    onChange={e => updateField('firstName', e.target.value)}
                    disabled={!connected}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="lastName">Cognome</Label>
                  <Input
                    id="lastName"
                    value={patientForm.lastName}
                    onChange={e => updateField('lastName', e.target.value)}
                    disabled={!connected}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="dateOfBirth">Data di nascita</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={patientForm.dateOfBirth}
                    onChange={e => updateField('dateOfBirth', e.target.value)}
                    disabled={!connected}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="sex">Sesso</Label>
                  <Input
                    id="sex"
                    type="select"
                    value={patientForm.sex}
                    onChange={e => updateField('sex', e.target.value)}
                    disabled={!connected}
                  >
                    {SEX_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="heightCm">Altezza (cm)</Label>
                  <Input
                    id="heightCm"
                    type="number"
                    min="50"
                    max="250"
                    value={patientForm.heightCm}
                    onChange={e => updateField('heightCm', e.target.value)}
                    disabled={!connected}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="bodyBuild">Corporatura</Label>
                  <Input
                    id="bodyBuild"
                    type="select"
                    value={patientForm.bodyBuild}
                    onChange={e => updateField('bodyBuild', e.target.value)}
                    disabled={!connected}
                  >
                    {BUILD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="waistCircumferenceCm">Circ. vita (cm)</Label>
                  <Input
                    id="waistCircumferenceCm"
                    type="number"
                    step="0.1"
                    min="40"
                    max="200"
                    value={patientForm.waistCircumferenceCm}
                    onChange={e => updateField('waistCircumferenceCm', e.target.value)}
                    disabled={!connected}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="hipCircumferenceCm">Circ. fianchi (cm)</Label>
                  <Input
                    id="hipCircumferenceCm"
                    type="number"
                    step="0.1"
                    min="40"
                    max="200"
                    value={patientForm.hipCircumferenceCm}
                    onChange={e => updateField('hipCircumferenceCm', e.target.value)}
                    disabled={!connected}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="weightKg">Peso attuale (kg)</Label>
                  <Input
                    id="weightKg"
                    type="number"
                    step="0.1"
                    min="5"
                    max="300"
                    value={patientForm.weightKg}
                    onChange={e => updateField('weightKg', e.target.value)}
                    disabled={!connected}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Button color="primary" onClick={savePatient} disabled={!connected || patientSaving}>
              {patientSaving ? <><Spinner size="sm" className="me-1" /> Salvataggio...</> : (selectedPatientId ? 'Aggiorna paziente' : 'Crea paziente')}
            </Button>
            </fieldset>
          </Form>
        </CardBody>
      </Card>}

      {embedded && (
        <Card className="bia-card">
          <CardBody>
            <CardTitle tag="h2">2. Paziente e peso</CardTitle>
            <p className="text-muted mb-3">
              La misurazione sarà associata a <strong>{selectedPatientName}</strong> e alla visita corrente.
            </p>
            <FormGroup className="mb-0 bia-visit-weight">
              <Label for="visitWeightKg">Peso rilevato (kg)</Label>
              <Input
                id="visitWeightKg"
                type="number"
                step="0.1"
                min="5"
                max="300"
                value={patientForm.weightKg}
                onChange={e => updateField('weightKg', e.target.value)}
                disabled={measuring || calibrating}
              />
            </FormGroup>
          </CardBody>
        </Card>
      )}

      {/* Step 3: Misura */}
      <Card className="bia-card">
        <CardBody>
          <CardTitle tag="h2">3. Misurazione</CardTitle>
          <FormGroup>
            <Label for="biaAcquisitionProfile">Frequenze di acquisizione</Label>
            <Input id="biaAcquisitionProfile" type="select" value={acquisitionProfile}
              disabled={measuring || calibrating} aria-describedby="biaProfileHelp"
              onChange={event => {
                setAcquisitionProfile(event.target.value);
                setMeasureStatus('');
              }}>
              <option value="multifrequency">Multifrequenza · 5 / 50 / 100 kHz</option>
              <option value="classic">Classico · 50 / 100 kHz</option>
            </Input>
            <p id="biaProfileHelp" className="text-muted small mt-2">
              {acquisitionProfile === 'multifrequency'
                ? 'Modalità predefinita. Richiede uno strumento compatibile con i 5 kHz. Se una frequenza fallisce, la misura si interrompe senza passare al classico.'
                : 'Modalità a due frequenze: i dati a 5 kHz non vengono acquisiti né stimati.'}
            </p>
          </FormGroup>
          <p className="text-muted">
            Posiziona gli elettrodi sul paziente (mano e piede destro), poi avvia la misura.
          </p>
          <div className="d-flex gap-2 flex-wrap">
            <Button color="primary" onClick={handleMeasure} disabled={!canMeasure}>
              {measuring ? <><Spinner size="sm" className="me-1" /> Misura in corso...</> : 'Avvia misura'}
            </Button>
            <Button color="secondary" outline onClick={handleCalibrate} disabled={!connected || calibrating || measuring}>
              {calibrating ? <><Spinner size="sm" className="me-1" /> Calibrazione...</> : 'Calibra'}
            </Button>
          </div>
          {!measuring && !canMeasure && <p className="bia-measure-help">{!connected ? 'Collega BioSmart per iniziare.' : missingContext.length ? `Completa nella cartella: ${missingContext.join(', ')}.` : !validWeight ? 'Inserisci il peso rilevato tra 5 e 300 kg.' : deviceInfo?.isSupported === false ? 'Il modello collegato non è supportato.' : deviceInfo?.isCalibrated === false ? 'Completa la calibrazione prima di misurare.' : 'Attendi il completamento dell’operazione in corso.'}</p>}
          <div role="status" aria-live="polite" aria-atomic="true" className="mt-3">
            {measureStatus && <p className="mb-0">{measureStatus}</p>}
            {measuring && activeFrequency !== null && <p className="text-muted small mt-1 mb-0">
              Frequenza {BIA_ACQUISITION_PROFILES[acquisitionProfile].indexOf(activeFrequency) + 1} di {BIA_ACQUISITION_PROFILES[acquisitionProfile].length}. Mantieni la posizione fino al completamento.
            </p>}
          </div>
        </CardBody>
      </Card>

      {/* Step 4: Risultati */}
      {lastMeasurement && (
        <BiaResults
          measurement={lastMeasurement}
          composition={lastComposition}
          onExport={handleExport}
          onNewMeasure={handleNewMeasure}
        />
      )}

      {/* Log */}
      <details className="bia-log-section">
        <summary>Log comunicazione</summary>
        <pre className="bia-log">
          {logs.length === 0 ? 'Nessun log.' : logs.join('\n')}
          <span ref={logEndRef} />
        </pre>
      </details>
    </div>
  );
}

export default BiaAnalyzer;
